/**
 * ObjectivesListPage — el hub unificado de Campañas.
 *
 * Filtros en 2 niveles:
 *   1. Por origen (Automáticas / Sugeridas / Mías / Todas)
 *   2. Por estado (Activas / Pausadas / Pendientes IA / Borradores / etc.)
 *
 * También soporta deep-link via ?filter=<status> para que las notifs del
 * inbox lleven directo a la vista relevante.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus,
  Target,
  CheckCircle2,
  Sparkles,
  Loader2,
  AlertCircle,
  Search,
  Filter as FilterIcon,
  Wand2,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import { useObjectives } from "../hooks/useObjectives";
import CampaignCard from "../components/CampaignCard";
import SuggestionDetailModal from "../components/SuggestionDetailModal";
import AIReviewModal from "../components/AIReviewModal";
import TriggerStudio from "../components/TriggerStudio";
import { objectivesApi } from "../api";
import { dispatchInboxRefresh } from "../../../services/inbox-events";
import type { CampaignOrigin, Objective, ObjectiveStatus } from "../types";

const MOCK_BRAND_ID = "brand-demo";

type OriginFilter = CampaignOrigin | "all";
type StatusFilter = ObjectiveStatus | "all";

const ORIGIN_FILTERS: {
  key: OriginFilter;
  label: string;
  emoji: string;
}[] = [
  { key: "all", label: "Todas", emoji: "🌐" },
  { key: "automatic", label: "Automáticas", emoji: "⚡" },
  { key: "suggested", label: "Sugeridas", emoji: "✨" },
  { key: "custom", label: "Mías", emoji: "🎨" },
];

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Todos los estados" },
  { key: "active", label: "Activas" },
  { key: "paused", label: "Pausadas" },
  { key: "suggested", label: "Pendientes de decisión" },
  { key: "pending_review", label: "En revisión IA" },
  { key: "needs_changes", label: "Necesitan ajustes" },
  { key: "draft", label: "Borradores" },
  { key: "dismissed", label: "Descartadas" },
];

const ObjectivesListPage = () => {
  const {
    objectives,
    loading,
    error,
    busyId,
    activate,
    pause,
    duplicate,
    remove,
    refresh: refetch,
  } = useObjectives(MOCK_BRAND_ID);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [originFilter, setOriginFilter] = useState<OriginFilter>(() => {
    const o = searchParams.get("origin");
    return (o as OriginFilter) || "all";
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const s = searchParams.get("filter");
    return (s as StatusFilter) || "all";
  });

  // Modales
  const [activeSuggestion, setActiveSuggestion] = useState<Objective | null>(null);
  const [activeReview, setActiveReview] = useState<Objective | null>(null);
  const [activeStudio, setActiveStudio] = useState<Objective | null>(null);

  // Sync URL params with filters
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (originFilter === "all") next.delete("origin");
    else next.set("origin", originFilter);
    if (statusFilter === "all") next.delete("filter");
    else next.set("filter", statusFilter);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originFilter, statusFilter]);

  // Deep-link ?review=<id> — abrir AIReviewModal cuando llega del wizard
  useEffect(() => {
    const reviewId = searchParams.get("review");
    if (!reviewId || loading) return;
    const obj = objectives.find((o) => o.id === reviewId);
    if (obj) {
      setActiveReview(obj);
      // Limpiar el param para que no se reabra en refresh
      const next = new URLSearchParams(searchParams);
      next.delete("review");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives, loading]);

  /* ─── Filtered list ─── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const includeHidden =
      statusFilter === "dismissed" || statusFilter === "archived";
    return objectives.filter((o) => {
      if (originFilter !== "all" && o.origin !== originFilter) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (q && !o.name.toLowerCase().includes(q)) return false;
      // Esconder dismissed/archived salvo que el filtro sea explícito
      if (
        !includeHidden &&
        (o.status === "dismissed" || o.status === "archived")
      )
        return false;
      return true;
    });
  }, [objectives, search, originFilter, statusFilter]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const active = objectives.filter((o) => o.status === "active");
    const suggested = objectives.filter((o) => o.status === "suggested");
    const needsChanges = objectives.filter(
      (o) => o.status === "needs_changes",
    );
    return {
      total: objectives.filter(
        (o) => o.status !== "dismissed" && o.status !== "archived",
      ).length,
      active: active.length,
      suggested: suggested.length,
      needsChanges: needsChanges.length,
    };
  }, [objectives]);

  /* ─── Handlers ─── */
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const handleDelete = (id: string, name: string) => {
    setPendingDelete({ id, name });
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    const ok = await remove(pendingDelete.id);
    setDeleteBusy(false);
    if (ok) toast.success("Campaña eliminada");
    else toast.error("Error al eliminar la campaña");
    setPendingDelete(null);
  };

  const handleCardClick = (obj: Objective) => {
    if (obj.origin === "suggested" && obj.status === "suggested") {
      setActiveSuggestion(obj);
      return;
    }
    if (
      obj.origin === "custom" &&
      (obj.status === "pending_review" || obj.status === "needs_changes")
    ) {
      setActiveReview(obj);
      return;
    }
    // Dismissed: reabrir el modal de sugerencia tratándola como "suggested" de
    // nuevo para que el usuario pueda aceptarla/editarla/volver a descartar.
    if (obj.status === "dismissed") {
      const reactivated = { ...obj, status: "suggested" as const };
      setActiveSuggestion(reactivated);
      return;
    }
    // default: navegar al dashboard (lo hace el Link interno de la card)
  };

  const handleAcceptSuggestion = async (id: string) => {
    const r = await objectivesApi.acceptSuggestion(id);
    if (r.success) {
      toast.success("¡Sugerencia aceptada! La campaña ya está activa.");
      setActiveSuggestion(null);
      refetch();
      // El backend pushea notif "Sugerencia activada" — refrescar el bell ya
      dispatchInboxRefresh();
    }
  };

  const handleDismissSuggestion = async (id: string, reason: string) => {
    const r = await objectivesApi.dismissSuggestion(id, reason);
    if (r.success) {
      setActiveSuggestion(null);
      refetch();
    }
  };

  const handleEditSuggestion = async (
    id: string,
    updates: { name?: string; messagePreview?: string },
  ) => {
    const r = await objectivesApi.editSuggestion(id, updates);
    if (r.success) {
      setActiveSuggestion(null);
      refetch();
    }
  };

  const handleRunReview = async (id: string) => {
    const r = await objectivesApi.runAIReview(id);
    if (r.success) {
      refetch();
      // Backend pushea notif (approved o needs_changes) — refrescar el bell
      dispatchInboxRefresh();
      // Mantenemos el modal abierto con el resultado
      setActiveReview(r.data);
    }
  };

  const handleResubmit = async (
    id: string,
    payload: { name?: string; messagePreview?: string },
  ) => {
    // Guardar cambios + reenviar a revisión
    await objectivesApi.updateMessage(id, payload);
    const sr = await objectivesApi.submitForReview(id);
    if (sr.success) {
      refetch();
      setActiveReview(sr.data);
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                Campañas
              </h1>
              {!loading && (
                <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">
                  {stats.total}
                </span>
              )}
            </div>
            <p className="text-slate-500 text-base">
              Deenex te da campañas listas, te sugiere otras según oportunidad y
              valida las tuyas antes de salir. Todo en un solo lugar.
            </p>
          </div>
          <Link to="/objectives/new">
            <Button variant="primary">
              <Plus size={16} />
              Nueva campaña
            </Button>
          </Link>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Target}
            label="En total"
            value={stats.total}
            iconClass="text-slate-500"
            bgClass="bg-slate-100"
          />
          <StatCard
            icon={CheckCircle2}
            label="Activas"
            value={stats.active}
            iconClass="text-emerald-600"
            bgClass="bg-emerald-100"
          />
          <StatCard
            icon={Sparkles}
            label="Sugerencias nuevas"
            value={stats.suggested}
            iconClass="text-violet-600"
            bgClass="bg-violet-100"
            pulse={stats.suggested > 0}
            onClick={() => {
              setOriginFilter("suggested");
              setStatusFilter("suggested");
            }}
          />
          <StatCard
            icon={AlertTriangle}
            label="Necesitan ajustes"
            value={stats.needsChanges}
            iconClass="text-amber-600"
            bgClass="bg-amber-100"
            pulse={stats.needsChanges > 0}
            onClick={() => {
              setOriginFilter("custom");
              setStatusFilter("needs_changes");
            }}
          />
        </div>

        {/* Filtros por origen (primary) */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Tipo
            </span>
            {ORIGIN_FILTERS.map((f) => {
              const active = originFilter === f.key;
              // Contar consistente: excluir dismissed/archived salvo que el
              // usuario haya pedido específicamente esos estados.
              const includeHidden =
                statusFilter === "dismissed" ||
                statusFilter === "archived";
              const matchesOrigin = (o: Objective) =>
                f.key === "all" || o.origin === f.key;
              const count = objectives.filter(
                (o) =>
                  matchesOrigin(o) &&
                  (includeHidden ||
                    (o.status !== "dismissed" && o.status !== "archived")),
              ).length;
              return (
                <button
                  key={f.key}
                  onClick={() => setOriginFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    active
                      ? "bg-violet-600 text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span className="text-sm">{f.emoji}</span>
                  {f.label}
                  {count > 0 && (
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        active ? "bg-white/20 text-white" : "bg-white text-slate-500"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filtros por status (secondary) + search */}
          <div className="flex flex-col md:flex-row gap-3 pt-3 border-t border-slate-100">
            <div className="relative flex-1">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <label htmlFor="campaign-search" className="sr-only">
                Buscar campaña por nombre
              </label>
              <input
                id="campaign-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre…"
                aria-label="Buscar campaña por nombre"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <FilterIcon size={14} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
              <label htmlFor="status-filter" className="sr-only">
                Filtrar por estado
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as StatusFilter)
                }
                aria-label="Filtrar por estado de campaña"
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* Grid / Loading / Empty */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <Loader2 className="animate-spin text-violet-500 mb-4" size={36} />
            <p className="text-slate-500">Cargando campañas…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            hasObjectives={objectives.length > 0}
            originFilter={originFilter}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((o) => (
                <CampaignCard
                  key={o.id}
                  campaign={o}
                  busy={busyId === o.id}
                  onActivate={async () => {
                    const ok = await activate(o.id);
                    if (ok) toast.success(`"${o.name}" activada`);
                    else toast.error("No se pudo activar la campaña");
                  }}
                  onPause={async () => {
                    const ok = await pause(o.id);
                    if (ok) toast.success(`"${o.name}" pausada`);
                    else toast.error("No se pudo pausar la campaña");
                  }}
                  onDuplicate={() => duplicate(o.id)}
                  onDelete={() => handleDelete(o.id, o.name)}
                  onClick={() => handleCardClick(o)}
                  onOpenStudio={() => setActiveStudio(o)}
                />
              ))}
            </div>

            {/* Bloque largo al final: agregar campaña personalizada (directo a /custom) */}
            <Link
              to="/objectives/new/custom"
              className="block mt-4 relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-r from-slate-50 via-white to-violet-50/40 hover:border-violet-400 hover:from-violet-50/40 hover:via-white hover:to-fuchsia-50/40 transition-all duration-300 group"
            >
              <div className="absolute -right-12 -top-12 w-48 h-48 bg-gradient-to-br from-violet-200/40 to-fuchsia-200/30 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-500" />

              <div className="relative flex items-center gap-4 px-6 py-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md shadow-violet-500/30 group-hover:scale-105 group-hover:rotate-3 transition-transform duration-300 flex-shrink-0">
                  <Wand2 size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-slate-900">
                      Agregar campaña personalizada
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                      Pasa por revisión IA
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    ¿Querés algo distinto? Armala desde cero. Deenex la valida
                    antes de que salga.
                  </p>
                </div>
                <div className="flex-shrink-0 bg-white border border-violet-200 text-violet-700 group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all duration-300 shadow-sm">
                  Crear
                  <ArrowRight
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </div>
              </div>
            </Link>
          </>
        )}
      </div>

      {/* Modales */}
      {activeSuggestion && (
        <SuggestionDetailModal
          campaign={activeSuggestion}
          busy={busyId === activeSuggestion.id}
          onClose={() => setActiveSuggestion(null)}
          onAccept={() => handleAcceptSuggestion(activeSuggestion.id)}
          onDismiss={(reason) =>
            handleDismissSuggestion(activeSuggestion.id, reason)
          }
          onEdit={(updates) =>
            handleEditSuggestion(activeSuggestion.id, updates)
          }
        />
      )}

      {activeReview && (
        <AIReviewModal
          campaign={activeReview}
          onClose={() => setActiveReview(null)}
          onRunReview={() => handleRunReview(activeReview.id)}
          onResubmit={(payload) => handleResubmit(activeReview.id, payload)}
        />
      )}

      {pendingDelete && (
        <ConfirmDangerModal
          title="Borrar campaña"
          description={`¿Borrar la campaña "${pendingDelete.name}"? Esta acción no se puede deshacer.`}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
          busy={deleteBusy}
        />
      )}

      {activeStudio && (
        <TriggerStudio
          campaign={activeStudio}
          onClose={() => setActiveStudio(null)}
          onSaved={() => {
            setActiveStudio(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

/* ─── Sub-components ─── */

interface StatCardProps {
  icon: typeof Target;
  label: string;
  value: number;
  iconClass: string;
  bgClass: string;
  pulse?: boolean;
  onClick?: () => void;
}

function StatCard({
  icon: Icon,
  label,
  value,
  iconClass,
  bgClass,
  pulse,
  onClick,
}: StatCardProps) {
  const interactive = !!onClick && value > 0;
  const Container = interactive ? "button" : "div";
  return (
    <Container
      onClick={interactive ? onClick : undefined}
      className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-3 text-left w-full ${
        interactive
          ? "hover:border-violet-300 hover:shadow-md transition-all cursor-pointer"
          : ""
      }`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgClass} ${pulse && value > 0 ? "ring-2 ring-offset-2 ring-violet-200 animate-pulse" : ""}`}
      >
        <Icon size={18} className={iconClass} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 tracking-tight leading-none">
          {value}
        </p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </Container>
  );
}

function EmptyState({
  hasObjectives,
  originFilter,
}: {
  hasObjectives: boolean;
  originFilter: OriginFilter;
}) {
  // Frases armadas a propósito (no template) para que la gramática cierre
  const noMatchMsg: Record<OriginFilter, string> = {
    all: "Ninguna campaña coincide con los filtros",
    automatic: "Ninguna campaña automática coincide con los filtros",
    suggested: "No hay sugerencias que coincidan con los filtros",
    custom: "Ninguna campaña tuya coincide con los filtros",
  };
  return (
    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
      <div className="w-16 h-16 bg-violet-50 rounded-2xl flex items-center justify-center mb-4">
        <Target size={28} className="text-violet-500" />
      </div>
      <p className="text-slate-700 font-medium mb-2">
        {hasObjectives
          ? noMatchMsg[originFilter]
          : "Todavía no tenés campañas creadas"}
      </p>
      <p className="text-slate-500 text-sm mb-5 max-w-md text-center leading-relaxed">
        {hasObjectives
          ? "Probá ajustar los filtros o el término de búsqueda."
          : "Empezá con uno de los 18 presets (efemérides, carrito abandonado, cumpleaños…) o creá una desde cero. Deenex te ayuda con lo demás."}
      </p>
      {!hasObjectives && (
        <Link to="/objectives/new">
          <Button variant="primary">
            <Plus size={15} />
            Crear mi primera campaña
          </Button>
        </Link>
      )}
    </div>
  );
}

export default ObjectivesListPage;
